// useCampPicker — encapsulates the "second-step" flow for parent
// properties that host multiple bookable camps (currently only
// "Bivouacs La Belle Étoile").
//
// The picker opens BEFORE any block is added: the parent card behaves
// like a category, not a bookable room. When the guest confirms one or
// more camps, we spawn one independent block per camp so each keeps its
// own dates / rooms / notes. Cancelling doesn't mutate state.
//
// Consumers pass `blocks` (read-only, for edit-mode preselection) and
// `mutate` (from `useBookingBlocks`). The hook returns:
//   • campPicker — modal state (null when closed)
//   • toggleSlug(slug) — main toggle handler; opens picker for camp
//     parents, or add/remove the row directly for regular properties
//   • openCampEditor(slug) — opens picker in "edit" mode with the
//     already-selected camps preselected
//   • closeCampPicker() — dismisses the modal (no state changes)
//   • onCampConfirmed(campSlugs) — call this from the modal's Confirm
//     callback; creates / re-shapes the blocks accordingly
//
// Extracted verbatim from `HomeBookingWidget.jsx` — behaviour is 1:1
// with the original inline handlers.

import { useCallback, useState } from "react";
import { getCampsForHotel } from "@/lib/bookingCamps";
import { newBlock } from "@/components/booking/homeBookingWidgetHelpers";

export default function useCampPicker({ blocks, mutate }) {
  // null | { hotelSlug, mode: "add"|"edit", initialCampSlugs?: string[] }
  const [campPicker, setCampPicker] = useState(null);

  const closeCampPicker = useCallback(() => setCampPicker(null), []);

  const toggleSlug = useCallback(
    (slug) => {
      const camps = getCampsForHotel(slug);
      const hasAny = blocks.some((b) => b.slug === slug);
      if (hasAny) {
        mutate((cur) => cur.filter((b) => b.slug !== slug));
        return;
      }
      if (camps.length > 0) {
        setCampPicker({ hotelSlug: slug, mode: "add", initialCampSlugs: [] });
        return;
      }
      mutate((cur) => [...cur, newBlock(slug)]);
    },
    [blocks, mutate]
  );

  // Replace the set of camps for a given hotel parent by spawning ONE
  // block per camp. Blocks whose camp is no longer selected are
  // removed; blocks whose camp is still selected are preserved
  // (dates/rooms/notes kept); newly selected camps are appended.
  const setBlockCampSlugs = useCallback(
    (slug, campSlugs) => {
      mutate((cur) => {
        const notThisHotel = cur.filter((b) => b.slug !== slug);
        const thisHotel = cur.filter((b) => b.slug === slug && b.campSlug);
        const kept = thisHotel.filter((b) => campSlugs.includes(b.campSlug));
        const keptSet = new Set(kept.map((b) => b.campSlug));
        const additions = campSlugs
          .filter((cs) => cs && !keptSet.has(cs))
          .map((cs) => newBlock(slug, cs));
        // Preserve the picker's declared order.
        const orderedForHotel = campSlugs
          .map(
            (cs) =>
              kept.find((b) => b.campSlug === cs) ||
              additions.find((b) => b.campSlug === cs)
          )
          .filter(Boolean);
        return [...notThisHotel, ...orderedForHotel];
      });
    },
    [mutate]
  );

  const openCampEditor = useCallback(
    (slug) => {
      const existing = blocks
        .filter((b) => b.slug === slug && b.campSlug)
        .map((b) => b.campSlug);
      setCampPicker({
        hotelSlug: slug,
        mode: "edit",
        initialCampSlugs: existing,
      });
    },
    [blocks]
  );

  const onCampConfirmed = useCallback(
    (campSlugs) => {
      if (!campPicker) return;
      const { hotelSlug, mode } = campPicker;
      if (mode === "edit") {
        setBlockCampSlugs(hotelSlug, campSlugs);
      } else {
        mutate((cur) => [
          ...cur,
          ...campSlugs.filter(Boolean).map((cs) => newBlock(hotelSlug, cs)),
        ]);
      }
      setCampPicker(null);
    },
    [campPicker, setBlockCampSlugs, mutate]
  );

  return {
    campPicker,
    toggleSlug,
    openCampEditor,
    closeCampPicker,
    onCampConfirmed,
  };
}
